import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClientType } from '@app/core/state/types';
import { TranslocoModule } from '@jsverse/transloco';
import { AutoFocusModule } from 'primeng/autofocus';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FocusTrapModule } from 'primeng/focustrap';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { BlFrontendButtonComponent } from 'src/app/core/wrappers/bl-frontend-button.component';
import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';
import { DialogData, DialogForm, DialogResult } from '../../form-dialog-base/form-dialog-base.component';

interface Data extends DialogData {
  client: Partial<ClientType>;
}

interface Result extends DialogResult {
  client: Partial<ClientType>;
}

interface Form extends DialogForm<unknown> {
  nameChecked: FormControl<boolean>;
  addressChecked: FormControl<boolean>;
}

const TRANSLATION_SCOPE = 'creditInformationUpdateDialog';

@Component({
  selector: 'app-kvk-information-update-dialog',
  template: `
    <ng-container *transloco="let t; prefix: scope">
      <form [formGroup]="form" (ngSubmit)="onSubmit()" pFocusTrap>
        <p
          class="font-semibold"
          [innerHTML]="
            t('collectingBasicInfoFor', { corporateIdentity: config.data.client.corporateIdentity }) | safeHtml
          "></p>
        <p>{{ t('selectedInfoWillWriteOverExisting') }}</p>
        <p class="font-semibold mt-5">{{ t('registeredInfo') }}</p>
        <div class="mb-2 flex items-start">
          <p-checkbox
            name="nameChecked"
            [binary]="true"
            formControlName="nameChecked"
            pAutoFocus
            [autofocus]="true"></p-checkbox>
          <label class="ml-2" for="nameChecked"
            ><strong>{{ t('name') }} </strong>{{ config.data.client.name }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="addressChecked" [binary]="true" formControlName="addressChecked"></p-checkbox>
          <label class="ml-2" for="addressChecked"
            ><strong>{{ t('address') }} </strong>{{ config.data.client.address }}, {{ config.data.client.zipCode }}
            {{ config.data.client.city }}</label
          >
        </div>
        <div class="flex flex-row w-full justify-end mt-4">
          <bl-frontend-button
            variant="secondary"
            class="mr-2"
            (onclick)="close({ client: null })"
            [text]="'shared.cancel' | transloco"></bl-frontend-button>
          <bl-frontend-button type="submit" [text]="'shared.update' | transloco"></bl-frontend-button>
        </div>
      </form>
    </ng-container>
  `,
  standalone: true,
  providers: [getTranslationProvider(TRANSLATION_SCOPE)],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CheckboxModule,
    FocusTrapModule,
    AutoFocusModule,
    TranslocoModule,
    BlFrontendButtonComponent,
    SafeHtmlPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KvkInformationUpdateDialog extends TranslatableFormDialogBaseComponent<Data, Result, Form> {
  onSubmit() {
    const name = this.getCheckedValue('nameChecked', 'name');
    const address = this.getCheckedValue('addressChecked', 'address');
    const zipCode = this.getCheckedValue('addressChecked', 'zipCode');
    const city = this.getCheckedValue('addressChecked', 'city');

    const client: Partial<ClientType> = {
      name,
      address,
      zipCode,
      city,
    };

    this.close({ client: this.cleanUpUnusedProps(client) });
  }

  protected override onInitialized = () => {
    const { name, address } = this.config.data.client;

    this.form = this.builder.group({
      nameChecked: new FormControl(Boolean(name)),
      addressChecked: new FormControl(Boolean(address)),
    });
  };

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: Record<string, string>,
  ): Omit<DynamicDialogConfig<Data>, 'data'> {
    return {
      header: translation.updateClientInfo,
      width: '500px',
      draggable: true,
      closable: true,
      modal: true,
      resizable: false,
    };
  }

  private getCheckedValue = (checkedKey: string, clientKey: string) =>
    this.form.value[checkedKey] ? this.config.data.client[clientKey] : null;

  private cleanUpUnusedProps(client: Partial<ClientType>): Partial<ClientType> {
    return Object.entries(client).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {} as Partial<ClientType>);
  }
}
